import { useState, useEffect, useRef, useCallback } from 'react';
import useLawMaker from '../../../hooks/useLawMaker';
import Select from 'react-select';
import {
  BodyBox,
  BodyBoxTitle,
  LawmakerContainer,
  LawmakerLeft,
  LawmakerRight,
  LawmakerForm,
  LawmakerFormGroup,
  LawmakerLabel,
  LawmakerButton,
  LawmakerLabelOr,
  LawmakerCheckboxContainer,
  LawmakerChamberDiv,
  LawmakerCheckbox,
  LawmakerTag,
  TagContent,
  RemoveTag,
  CheckContainer,
} from './Profile.Style';

export const LawMakerList = ({ Lawmakers, setLawmakers }) => {
  const selectInputRef = useRef();

  const { getLawmakersList } = useLawMaker();
  const [lawmakerList, setLawmakerList] = useState([]);
  const [lawmakerOptions, setLawmakerOptions] = useState([]);
  const [selectedLawmaker, setSelectedLawmaker] = useState(null);

  useEffect(() => {
    (async () => {
      const fetched = await getLawmakersList();
      setLawmakerList(fetched);

      const options = fetched.map((lawmaker) => ({
        value: lawmaker.pid,
        label: `${lawmaker.first} ${lawmaker.last} (${lawmaker.party[0]})`,
      }));
      setLawmakerOptions(options);
    })();
  }, []);

  const style1 = {
    control: (base) => ({
      ...base,
      border: '1px solid #ced4da !important',
      boxShadow: '1px solid #ced4da !important',
      '&:hover': {
        border: '1px solid #ced4da !important',
      },
    }),
    indicatorsContainer: (prevStyle, state) =>
      state.isMulti
        ? {
            ...prevStyle,
            display: 'none',
          }
        : null,
  };

  const handleRemove = useCallback(
    (pid) => {
      setLawmakers((current) => current.filter((e) => e.pid !== pid));
    },
    [setLawmakers]
  );

  const handleToggleLawmaker = useCallback(
    (lawmaker) => {
      const exists = Lawmakers.some((tag) => tag.pid === lawmaker.pid);
      if (exists) {
        handleRemove(lawmaker.pid);
      } else {
        const obj = {
          pid: lawmaker.pid,
          first: lawmaker.first,
          last: lawmaker.last,
          party: lawmaker.party,
        };
        setLawmakers((current) => [...current, obj]);
      }
    },
    [Lawmakers, handleRemove, setLawmakers]
  );

  const isLawmakerSelected = (lawmaker) =>
    Lawmakers.some((tag) => tag.pid === lawmaker.pid);

  const handleAddSelectedLawmaker = () => {
    if (!selectedLawmaker) return;

    const exists = Lawmakers.some((tag) => tag.pid === selectedLawmaker.value);
    if (!exists) {
      const found = lawmakerList.find((l) => l.pid === selectedLawmaker.value);
      if (found) {
        const obj = {
          pid: found.pid,
          first: found.first,
          last: found.last,
          party: found.party,
        };
        setLawmakers((current) => [...current, obj]);
      }
    }
    setSelectedLawmaker(null);
  };

  return (
    <BodyBox>
      <LawmakerContainer>
        <LawmakerLeft>
          <BodyBoxTitle>ADD LAWMAKERS</BodyBoxTitle>
          <LawmakerForm>
            <LawmakerFormGroup>
              <LawmakerLabel>Search Lawmaker</LawmakerLabel>
              <Select
                ref={selectInputRef}
                value={selectedLawmaker}
                options={lawmakerOptions}
                styles={style1}
                placeholder="Search by name"
                onChange={setSelectedLawmaker}
                isClearable
              />
            </LawmakerFormGroup>
            <div style={{ position: 'relative', height: '100%' }}>
              <LawmakerButton onClick={handleAddSelectedLawmaker} type="button">
                Add
              </LawmakerButton>
            </div>
          </LawmakerForm>
          <LawmakerLabelOr>or</LawmakerLabelOr>
          <BodyBoxTitle>SELECT LAWMAKERS</BodyBoxTitle>
          <LawmakerCheckboxContainer>
            <LawmakerChamberDiv>
              <LawmakerLabelOr>
                {lawmakerList.some((lm) => lm.chamber === 'House') ? 'House' : 'Assembly'}
              </LawmakerLabelOr>
              <LawmakerCheckbox>
                {lawmakerList
                  .filter((lm) => lm.chamber === 'Assembly' || lm.chamber === 'House')
                  .map((lawmaker) => (
                    <CheckContainer key={lawmaker.pid}>
                      {lawmaker.first} {lawmaker.last} ({lawmaker.party[0]})
                      <input
                        type="checkbox"
                        checked={isLawmakerSelected(lawmaker)}
                        onChange={() => handleToggleLawmaker(lawmaker)}
                      />
                      <span />
                    </CheckContainer>
                  ))}
              </LawmakerCheckbox>
            </LawmakerChamberDiv>
            <LawmakerChamberDiv>
              <LawmakerLabelOr>Senate</LawmakerLabelOr>
              <LawmakerCheckbox>
                {lawmakerList
                  .filter((lm) => lm.chamber === 'Senate')
                  .map((lawmaker) => (
                    <CheckContainer key={lawmaker.pid}>
                      {lawmaker.first} {lawmaker.last} ({lawmaker.party[0]})
                      <input
                        type="checkbox"
                        checked={isLawmakerSelected(lawmaker)}
                        onChange={() => handleToggleLawmaker(lawmaker)}
                      />
                      <span />
                    </CheckContainer>
                  ))}
              </LawmakerCheckbox>
            </LawmakerChamberDiv>
          </LawmakerCheckboxContainer>
        </LawmakerLeft>
        <LawmakerRight>
          <BodyBoxTitle>LAWMAKERS ADDED</BodyBoxTitle>
          {Lawmakers.map((tag) => (
            <LawmakerTag key={tag.pid}>
              <RemoveTag
                onClick={() => handleRemove(tag.pid)}
                role="button"
                aria-label={`Remove ${tag.first} ${tag.last}`}
              />
              <TagContent>
                {tag.first} {tag.last} ({tag.party[0]})
              </TagContent>
            </LawmakerTag>
          ))}
        </LawmakerRight>
      </LawmakerContainer>
    </BodyBox>
  );
};
